import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, CheckCircle2, AlertTriangle, HelpCircle,
  Pencil, RefreshCcw, ShieldQuestion, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_STYLE = {
  CONFIRMED_FROM_DOCUMENT: { c: "bg-emerald-100 text-emerald-800 border-emerald-300", l: "Confirmado no documento" },
  CONFIRMED_FROM_TITLE:    { c: "bg-emerald-100 text-emerald-800 border-emerald-300", l: "Confirmado no título" },
  USER_CONFIRMED:          { c: "bg-slate-900 text-white border-slate-900",           l: "Confirmado por você" },
  SYSTEM_INFERRED:         { c: "bg-amber-100 text-amber-800 border-amber-300",       l: "Inferido — precisa confirmar" },
  CONFLICTING:             { c: "bg-rose-100 text-rose-800 border-rose-300",          l: "Conflito entre fontes" },
  REVIEW_REQUIRED:         { c: "bg-rose-100 text-rose-800 border-rose-300",          l: "Revisão necessária" },
  MISSING:                 { c: "bg-slate-100 text-slate-600 border-slate-300",       l: "Faltando" },
};

const fmtValue = (p) => {
  if (p.value === null || p.value === undefined) return "—";
  if (p.datatype === "MONEY") {
    const cents = Number(p.value || 0);
    return cents.toLocaleString("pt-BR", { style: "currency", currency: "BRL",
                                             minimumFractionDigits: 2 }).replace("R$", "R$ ").replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.")
      .replace(",", "TEMP").replace(/\./g, ",").replace("TEMP", ".").replace("R$ ,", "R$ ")
      // fallback simples: cents/100
      || `R$ ${(cents/100).toFixed(2)}`;
  }
  if (p.datatype === "PERCENT") return `${(Number(p.value) * 100).toFixed(1)}%`;
  if (p.datatype === "BOOL") return p.value ? "Sim" : "Não";
  return String(p.value);
};

// Formatação BRL segura
const brl = (cents) => {
  const v = Number(cents || 0) / 100;
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

export default function LiquidacaoParametros() {
  const { id } = useParams();
  const nav = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      api.get(`/liq/cases/${id}`),
      api.get(`/liq/cases/${id}/parameters`),
    ]);
    setCaseData(c.data);
    setRows(p.data.rows || []);
    setCounts(p.data.counts || {});
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const extract = async () => {
    setBusy(true);
    try {
      await api.post(`/liq/cases/${id}/parameters/extract`);
      toast.success("Extração iniciada. Aguarde alguns segundos…");
      // polling curto
      for (let i = 0; i < 15; i++) {
        await new Promise(r => setTimeout(r, 2000));
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao extrair parâmetros");
    } finally { setBusy(false); }
  };

  const confirm = async (p) => {
    try {
      await api.post(`/liq/parameters/${p.id}/confirm`);
      toast.success(`${p.label} confirmado.`);
      await load();
    } catch { toast.error("Falha ao confirmar."); }
  };
  const patch = async (p, value) => {
    try {
      await api.patch(`/liq/parameters/${p.id}`, { value, status: "USER_CONFIRMED" });
      toast.success(`${p.label} atualizado.`);
      await load();
    } catch { toast.error("Falha ao salvar."); }
  };
  const dontKnow = async (p) => {
    try {
      await api.post(`/liq/parameters/${p.id}/mark-unknown`);
      toast.info(`${p.label} marcado como pendente.`);
      await load();
    } catch { toast.error("Falha."); }
  };

  const visible = rows.filter(p => {
    if (filter === "all") return true;
    if (filter === "pending") return ["MISSING", "SYSTEM_INFERRED",
                                        "CONFLICTING", "REVIEW_REQUIRED"].includes(p.status);
    if (filter === "confirmed") return ["CONFIRMED_FROM_DOCUMENT",
                                          "CONFIRMED_FROM_TITLE", "USER_CONFIRMED"].includes(p.status);
    return p.status === filter;
  });

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-6" data-testid="liq-params">
      <div className="max-w-6xl mx-auto space-y-4">
        <Link to={`/calculos/liquidacao-trabalhista/${id}`}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="liq-params-back">
          <ArrowLeft className="w-3.5 h-3.5"/> Voltar ao caso
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
              Hermes Calc · Parâmetros
            </div>
            <h1 className="font-heading text-3xl font-semibold text-slate-900">
              Parâmetros do cálculo
            </h1>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Extraímos automaticamente do título executivo. <b>Você precisa confirmar
              cada valor antes de calcular</b>. Nada é inventado — o que não foi
              localizado fica marcado como <span className="font-mono text-xs">MISSING</span> para você preencher.
            </p>
          </div>
          <button onClick={extract} disabled={busy}
            data-testid="liq-params-extract"
            className="h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-2">
            <RefreshCcw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`}/>
            {busy ? "Extraindo…" : "Extrair do processo"}
          </button>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CountCard label="Confirmados"
                     value={(counts.CONFIRMED_FROM_DOCUMENT||0) + (counts.CONFIRMED_FROM_TITLE||0) + (counts.USER_CONFIRMED||0)}
                     tone="ok" testid="cnt-confirmed"/>
          <CountCard label="Inferidos (revisar)" value={counts.SYSTEM_INFERRED || 0} tone="warn" testid="cnt-inferred"/>
          <CountCard label="Conflitos" value={(counts.CONFLICTING||0) + (counts.REVIEW_REQUIRED||0)} tone="danger" testid="cnt-conflict"/>
          <CountCard label="Pendentes / MISSING" value={counts.MISSING || 0} tone={counts.MISSING ? "danger" : ""} testid="cnt-missing"/>
        </div>

        {/* Filtros */}
        <div className="flex items-center gap-2" data-testid="liq-params-filters">
          {[
            {k: "all", l: "Todos"},
            {k: "pending", l: "Pendentes"},
            {k: "confirmed", l: "Confirmados"},
            {k: "MISSING", l: "Faltando"},
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              data-testid={`liq-params-filter-${f.k}`}
              className={`h-9 px-3 rounded-lg text-xs font-medium border ${
                filter === f.k ? "bg-slate-900 text-white border-slate-900"
                               : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
              {f.l}
            </button>
          ))}
        </div>

        {/* Lista */}
        {visible.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center"
               data-testid="liq-params-empty">
            <ShieldQuestion className="w-10 h-10 text-slate-300 mx-auto mb-3"/>
            <div className="text-slate-700 font-medium">
              Nenhum parâmetro extraído ainda.
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Clique em "Extrair do processo" para o Hermes analisar as decisões
              já enviadas e propor os parâmetros.
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="liq-params-rows">
            {visible.map(p => (
              <ParamRow key={p.id} p={p}
                expanded={!!expanded[p.id]}
                onToggle={() => setExpanded(e => ({...e, [p.id]: !e[p.id]}))}
                onConfirm={() => confirm(p)}
                onPatch={(v) => patch(p, v)}
                onDontKnow={() => dontKnow(p)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CountCard({ label, value, tone, testid }) {
  const cls =
    tone === "ok"     ? "border-emerald-200 bg-emerald-50 text-emerald-900" :
    tone === "warn"   ? "border-amber-200 bg-amber-50 text-amber-900" :
    tone === "danger" ? "border-rose-200 bg-rose-50 text-rose-900" :
                         "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-xl border p-3 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function ParamRow({ p, expanded, onToggle, onConfirm, onPatch, onDontKnow }) {
  const st = STATUS_STYLE[p.status] || STATUS_STYLE.MISSING;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.value ?? "");
  useEffect(() => setDraft(p.value ?? ""), [p.value]);

  const save = () => {
    let v = draft;
    if (p.datatype === "NUMBER" || p.datatype === "MONEY") v = Number(draft);
    if (p.datatype === "PERCENT") v = Number(draft);
    if (p.datatype === "BOOL") v = draft === true || draft === "true";
    onPatch(v);
    setEditing(false);
  };

  return (
    <div className={`rounded-xl border-l-4 ${
        p.status === "MISSING" ? "border-rose-400 bg-white" :
        p.status === "SYSTEM_INFERRED" ? "border-amber-400 bg-white" :
        p.status === "USER_CONFIRMED" ? "border-slate-900 bg-white" :
        "border-emerald-400 bg-white"
      } border border-slate-200 p-4`}
      data-testid={`liq-param-${p.key}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-slate-900">{p.label}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold border ${st.c}`}>
              {st.l}
            </span>
            {p.confidence != null && p.status !== "MISSING" && (
              <span className="text-[10px] text-slate-500">
                confiança {Math.round((p.confidence || 0) * 100)}%
              </span>
            )}
          </div>
          <div className="mt-1 text-xs font-mono text-slate-400">{p.key}</div>

          {!editing ? (
            <div className="mt-2 text-lg font-mono text-slate-900"
                 data-testid={`liq-param-value-${p.key}`}>
              {fmtValue(p)}
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2">
              {p.datatype === "BOOL" ? (
                <select value={String(draft)}
                  onChange={e => setDraft(e.target.value === "true")}
                  className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm"
                  data-testid={`liq-param-input-${p.key}`}>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              ) : (
                <input value={draft ?? ""}
                  onChange={e => setDraft(e.target.value)}
                  placeholder={p.datatype === "MONEY" ? "em centavos (250000 = R$ 2.500,00)"
                                : p.datatype === "PERCENT" ? "0.50 = 50%"
                                : p.datatype === "DATE" ? "YYYY-MM-DD"
                                : ""}
                  className="flex-1 h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm font-mono"
                  data-testid={`liq-param-input-${p.key}`}/>
              )}
              <button onClick={save}
                className="h-10 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium"
                data-testid={`liq-param-save-${p.key}`}>Salvar</button>
              <button onClick={() => setEditing(false)}
                className="h-10 px-3 rounded-lg bg-white border border-slate-200 text-xs">
                Cancelar
              </button>
            </div>
          )}

          {expanded && (
            <div className="mt-3 space-y-2 text-xs text-slate-600 bg-slate-50 rounded-lg p-3">
              <div><b>Por que preciso deste parâmetro?</b> {p.why}</div>
              {p.source_excerpt && (
                <div>
                  <b>Trecho encontrado:</b>
                  <div className="mt-1 p-2 bg-white border border-slate-200 rounded text-slate-800 italic">
                    "{p.source_excerpt}"
                  </div>
                </div>
              )}
              {p.extraction_reason && (
                <div><b>Como chegamos aqui:</b> {p.extraction_reason}</div>
              )}
              {p.history?.length > 0 && (
                <div>
                  <b>Histórico ({p.history.length}):</b>
                  <ul className="mt-1 space-y-0.5 pl-3">
                    {p.history.slice(-5).reverse().map((h, i) => (
                      <li key={i} className="list-disc">
                        {String(h.value)} — {h.status} — {new Date(h.changed_at).toLocaleString("pt-BR")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1.5 shrink-0">
          {!editing && p.status !== "MISSING" && p.status !== "USER_CONFIRMED" && (
            <button onClick={onConfirm}
              className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium inline-flex items-center gap-1"
              data-testid={`liq-param-confirm-${p.key}`}>
              <CheckCircle2 className="w-3.5 h-3.5"/> Confirmar
            </button>
          )}
          {!editing && (
            <button onClick={() => setEditing(true)}
              className="h-8 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-xs inline-flex items-center gap-1"
              data-testid={`liq-param-edit-${p.key}`}>
              <Pencil className="w-3.5 h-3.5"/> Alterar
            </button>
          )}
          {!editing && p.status !== "MISSING" && (
            <button onClick={onDontKnow}
              className="h-8 px-3 rounded-lg bg-white border border-slate-200 hover:bg-rose-50 text-rose-700 text-xs inline-flex items-center gap-1"
              data-testid={`liq-param-unknown-${p.key}`}>
              <HelpCircle className="w-3.5 h-3.5"/> Não sei
            </button>
          )}
          <button onClick={onToggle}
            className="h-7 px-2 rounded-lg text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid={`liq-param-expand-${p.key}`}>
            {expanded ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
            {expanded ? "Ocultar" : "Detalhes"}
          </button>
        </div>
      </div>
    </div>
  );
}
